import { useEffect, useRef, useState } from 'react';
import ReactConfetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import html2canvas from 'html2canvas';
import { Download, Share2, UtensilsCrossed } from 'lucide-react';
import useStore from '@/store/useStore';
import { getSelectedMerchantId } from '@/lib/routes';

export default function EarnSuccessModal() {
  const { showEarnModal, earnModalData, closeEarnModal, merchantInfo, merchantUser } = useStore();
  const activeMerchantId = getSelectedMerchantId();
  const [dims, setDims] = useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 1024,
    h: typeof window !== 'undefined' ? window.innerHeight : 768,
  });
  const [showConfetti, setShowConfetti] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [capturedImage, setCapturedImage] = useState('');
  const shareCaptureRef = useRef(null);

  const storeUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${activeMerchantId ? `/store/${activeMerchantId}` : '/store'}`
    : '';
  const restaurantName = merchantInfo?.restaurant_name || 'this restaurant';
  const shareQuote = `I just earned ${earnModalData?.points_earned || 0} points at ${restaurantName}! 🍕 Join me and start earning rewards too: ${storeUrl}`;

  const brandColor = typeof window !== 'undefined'
    ? (window.getComputedStyle(document.documentElement).getPropertyValue('--brand-solid') || '').trim() || '#0E7A62'
    : '#0E7A62';

  useEffect(() => {
    if (showEarnModal) { setShowConfetti(true); const t = setTimeout(() => setShowConfetti(false), 4000); return () => clearTimeout(t); }
  }, [showEarnModal]);

  useEffect(() => {
    const h = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', h); return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    if (!showEarnModal) {
      setShareOpen(false);
      setShareMessage('');
      setCapturedImage('');
      setCapturing(false);
    }
  }, [showEarnModal]);

  if (!earnModalData) return null;
  const progress = earnModalData.new_balance ? Math.min((earnModalData.new_balance / 3000) * 100, 100) : 50;

  const getCapturedImage = async () => {
    if (capturedImage) return capturedImage;
    if (!shareCaptureRef.current) return '';

    setCapturing(true);
    setShareMessage('');
    try {
      const canvas = await html2canvas(shareCaptureRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      const dataUrl = canvas.toDataURL('image/png');
      setCapturedImage(dataUrl);
      return dataUrl;
    } catch {
      setShareMessage('Unable to capture share image right now. Please try again.');
      return '';
    } finally {
      setCapturing(false);
    }
  };

  const handleDownloadShareImage = async () => {
    const dataUrl = await getCapturedImage();
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `savorrewards-earned-${Date.now()}.png`;
    a.click();
    setShareMessage('Image downloaded successfully.');
  };

  const handleWhatsAppShare = async () => {
    const dataUrl = await getCapturedImage();
    if (!dataUrl) return;
    try {
      if (!navigator.share) {
        // Fallback for desktop or non-supported browsers
        const encodedText = encodeURIComponent(shareQuote);
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
        setShareMessage('Opening WhatsApp...');
        return;
      }

      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `savorrewards-earned-${Date.now()}.png`, { type: 'image/png' });

      const shareData = {
        title: 'SavorRewards Achievement',
        text: shareQuote,
        url: storeUrl,
      };

      if (navigator.canShare && navigator.canShare({ ...shareData, files: [file] })) {
        await navigator.share({
          ...shareData,
          files: [file],
        });
        setShareMessage('Achievement shared successfully!');
      } else {
        // If files cannot be shared with text/url together, try without file as secondary fallback
        await navigator.share(shareData);
        setShareMessage('Link shared. (Image download not supported on this browser)');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error('Share failed', e);
        setShareMessage('Unable to share right now.');
      }
    }
  };

  return (
    <>
      {showConfetti && showEarnModal && (
        <div className="confetti-container"><ReactConfetti width={dims.w} height={dims.h} recycle={false} numberOfPieces={200} gravity={0.3} colors={[brandColor, '#fca5a5', '#fecdd3', '#fed7aa']} /></div>
      )}
      <Dialog open={showEarnModal} onOpenChange={closeEarnModal}>
        <DialogContent className="rounded-2xl max-w-sm mx-auto" data-testid="earn-success-modal">
          <AnimatePresence>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center space-y-4 py-2">
              <DialogTitle className="sr-only">Points Earned</DialogTitle>
              <DialogDescription className="sr-only">You earned reward points</DialogDescription>
              <div ref={shareCaptureRef} className="space-y-4 rounded-2xl bg-card border border-border p-4">
                <div className="w-16 h-16 rounded-full bg-brand-soft mx-auto flex items-center justify-center">
                  <UtensilsCrossed className="w-7 h-7 text-brand" />
                </div>
                <h2 className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  +{earnModalData.points_earned} Points Earned
                </h2>
                <p className="text-sm text-muted-foreground">
                  Amazing! You just fueled your next reward{earnModalData.customer_name ? ` at restaurant ${merchantUser?.restaurant_name}` : ''}.
                </p>
                <div className="bg-secondary/50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-xs font-bold">
                    <span className="uppercase tracking-wider text-muted-foreground">Next Milestone</span>
                    <span className="text-brand">{earnModalData.new_balance?.toLocaleString() || 0} / 3,000 pts</span>
                  </div>
                  <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full rounded-full gradient-brand transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {Math.max(3000 - (earnModalData.new_balance || 0), 0)} pts left to unlock a $25 Dining Credit.
                  </p>
                </div>
              </div>
              <button data-testid="earn-done-btn" onClick={closeEarnModal}
                className="w-full h-12 rounded-full gradient-brand-btn text-base font-semibold">Done</button>
              <button
                className="text-sm font-semibold text-brand inline-flex items-center gap-1"
                data-testid="earn-share-btn"
                onClick={() => setShareOpen((v) => !v)}
              >
                <Share2 className="w-4 h-4" /> Share Achievement
              </button>
              {shareOpen && (
                <div className="rounded-xl border border-border bg-card p-3 space-y-2 text-left">
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      type="button"
                      onClick={handleDownloadShareImage}
                      disabled={capturing}
                      className="h-9 rounded-lg border border-border hover:bg-secondary text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="earn-share-download-btn"
                    >
                      <Download className="w-4 h-4" /> {capturing ? 'Capturing...' : 'Download Image'}
                    </button>
                    <button
                      type="button"
                      onClick={handleWhatsAppShare}
                      disabled={capturing}
                      className="h-9 rounded-lg border border-border hover:bg-secondary text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="earn-share-whatsapp-btn"
                    >
                      <Share2 className="w-4 h-4" /> WhatsApp
                    </button>
                  </div>
                  {shareMessage && <p className="text-xs text-muted-foreground">{shareMessage}</p>}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  );
}
